import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import AuthShell from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { TurnstileGate, turnstileError } from "@/components/common/TurnstileGate";
import { useTurnstile } from "@/hooks/usePublicConfig";

// Map a FastAPI error detail (string | array | object) into a display string.
function errText(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  if (d && typeof d.msg === "string") return d.msg;
  return fallback;
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("request"); // 'request' | 'reset'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const { enabled: tsEnabled, siteKey: tsSiteKey } = useTurnstile();
  const tsRequired = tsEnabled && !!tsSiteKey;
  const [tsToken, setTsToken] = useState(null);
  const tsRef = useRef(null);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (tsRequired && !tsToken) return toast.error("Please complete the verification.");
    setLoading(true);
    try {
      await api.forgotPassword(email.trim(), tsToken || undefined);
    } catch (err) {
      // Reset the single-use Turnstile token so a retry gets a fresh one.
      setTsToken(null);
      tsRef.current?.reset();
      const { isTurnstile, message } = turnstileError(err);
      if (isTurnstile) {
        setLoading(false);
        return toast.error(message || "Security check failed — please try again.");
      }
      // Any other error is swallowed on purpose (no account enumeration).
    }
    // Always advance — the backend never reveals whether the email exists.
    toast.success("If an account exists, a reset code is on its way.");
    setStep("reset");
    setLoading(false);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the code from your email.");
    if (password.length < 8) return toast.error("New password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.resetPassword(email.trim(), code.trim(), password);
      toast.success("Password updated — please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(errText(err, "That code is invalid or expired. Request a new one."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={step === "request" ? "Reset your password" : "Enter your reset code"}
      subtitle={
        step === "request"
          ? "We'll email you a one-time code to set a new password."
          : `We sent a code to ${email}. Enter it below with your new password.`
      }
      footer={
        <Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-bloq-mint hover:underline" data-testid="back-to-login-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
        </Link>
      }
    >
      {step === "request" ? (
        <form onSubmit={requestCode} className="space-y-5" data-testid="forgot-request-form">
          <div className="space-y-2">
            <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-bloq-muted">Email</Label>
            <Input
              id="fp-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@bloqtalk.app"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="forgot-email-input"
            />
          </div>

          {tsRequired && (
            <TurnstileGate ref={tsRef} action="forgot-password" siteKey={tsSiteKey} onToken={setTsToken} className="flex justify-center" />
          )}

          <Button
            type="submit"
            disabled={loading || (tsRequired && !tsToken)}
            className="h-13 w-full rounded-full bloq-gradient py-3.5 text-base font-bold text-bloq-bg shadow-mint hover:opacity-90 disabled:opacity-60"
            data-testid="forgot-request-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={resetPassword} className="space-y-5" data-testid="forgot-reset-form">
          <div className="space-y-2">
            <Label htmlFor="fp-code" className="text-xs uppercase tracking-wider text-bloq-muted">Reset code</Label>
            <Input
              id="fp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter the code"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 tracking-widest text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="reset-code-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fp-pass" className="text-xs uppercase tracking-wider text-bloq-muted">New password</Label>
            <div className="relative">
              <Input
                id="fp-pass"
                type={show ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 pr-11 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-bloq-muted hover:text-white"
                data-testid="reset-toggle-password"
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fp-confirm" className="text-xs uppercase tracking-wider text-bloq-muted">Confirm new password</Label>
            <Input
              id="fp-confirm"
              type={show ? "text" : "password"}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
              className="h-12 rounded-xl border-white/12 bg-bloq-bg px-4 text-white placeholder:text-bloq-muted focus-visible:ring-bloq-mint"
              data-testid="reset-confirm-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="h-13 w-full rounded-full bloq-gradient py-3.5 text-base font-bold text-bloq-bg shadow-mint hover:opacity-90 disabled:opacity-60"
            data-testid="reset-submit-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
          </Button>

          <button
            type="button"
            onClick={() => { setStep("request"); setCode(""); setPassword(""); setConfirm(""); }}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-bloq-s2 py-2.5 text-sm text-bloq-muted transition-colors hover:text-white"
            data-testid="reset-resend-btn"
          >
            <MailCheck className="h-4 w-4" /> Use a different email / resend code
          </button>
        </form>
      )}
    </AuthShell>
  );
}
